import { lazy, Suspense, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useRoomModuleAddCommands } from '@/features/modules/useRoomModuleAddCommands';
import { useSceneCommandFailureNotice } from '@/features/modules/useSceneCommandFailureNotice';
import { useSelectionAxisKeys } from '@/features/scene/useSelectionAxisKeys';
import { useGlobalViewerKeys } from '@/features/scene/useGlobalViewerKeys';
import { useUiStore } from '@/shared/store/ui-store';
import { useViewerStore } from '@/shared/store/viewer-store';
import { useModels } from '@/shared/api/queries';
import { WorkbenchRuntimeProvider } from './workbench/runtime';
import { publishedModelEntry } from './published-model-route';

const CommandPalette = lazy(() => import('@/features/command-palette/CommandPalette').then((m) => ({ default: m.CommandPalette })));
const ShortcutsScreen = lazy(() => import('@/features/shortcuts/ShortcutsScreen').then((m) => ({ default: m.ShortcutsScreen })));
const PanelDetailSheet = lazy(() => import('@/features/material-editor/PanelDetailSheet').then((m) => ({ default: m.PanelDetailSheet })));
const BulkPanelDialog = lazy(() => import('@/features/material-editor/BulkPanelDialog').then((m) => ({ default: m.BulkPanelDialog })));
const OnboardingTour = lazy(() => import('@/features/onboarding/OnboardingTour').then((m) => ({ default: m.OnboardingTour })));
const GlobalModalHost = lazy(() => import('./GlobalModalHost').then((m) => ({ default: m.GlobalModalHost })));
const LuminousShell = lazy(() => import('./luminous/LuminousShell').then((m) => ({ default: m.LuminousShell })));

type ModelListEntry = {
  id?: string | null;
  name?: string | null;
  inputKind?: string | null;
  sourceKind?: string | null;
  entitiesCount?: number | null;
  modelRowsCount?: number | null;
  hasGlb?: boolean | null;
  error?: unknown;
};

export function firstUsableStartupModel<T extends ModelListEntry>(models: readonly T[]): T | undefined {
  const usable = models.filter((model) => Boolean(model.id)
    && !model.error
    && ((model.entitiesCount ?? 0) > 0 || (model.modelRowsCount ?? 0) > 0 || model.hasGlb === true));
  const isRoomScene = (model: T) => {
    const haystack = `${model.inputKind ?? ''} ${model.sourceKind ?? ''} ${model.name ?? ''} ${model.id ?? ''}`.toLowerCase();
    return haystack.includes('apartment')
      || haystack.includes('room')
      || haystack.includes('квартира')
      || haystack.includes('комната');
  };
  return usable.find(isRoomScene)
    ?? usable.find((model) => String(model.sourceKind ?? '').toLowerCase() !== 'holzcom_modules')
    ?? usable[0]
    ?? models.find((model) => Boolean(model.id) && !model.error)
    ?? models.find((model) => Boolean(model.id));
}

export function App() {
  const [showOnboarding, setShowOnboarding] = useState(false);
  const { data: models } = useModels();
  const activeModelId = useViewerStore((s) => s.activeModelId);
  const setActiveModel = useViewerStore((s) => s.setActiveModel);
  const appliedUrlModelRef = useRef(false);
  /* Вставка модуля в комнату исполняется здесь, а не в панели «Структура»: панель по
     умолчанию закрыта, и нажатая в каталоге кнопка «Добавить в сцену» тогда молча ничего не
     делала. Корневой владелец команды остаётся смонтирован при любом workspace. */
  useRoomModuleAddCommands();
  /* Отказ команды сцены больше не умирает в сторе: причина 503/422 с сервера видна. */
  useSceneCommandFailureNotice();
  /* `Esc` идёт ВВЕРХ по оси «комната → композиция → модуль → деталь» и снимает
     выделение только с верхнего уровня. Ветка Escape убрана из общего
     обработчика ниже: две ветки на одну клавишу означали бы, что подъём
     случается, а следом его тут же стирает `clearSelection`. */
  useSelectionAxisKeys();

  useLayoutEffect(() => {
    const entry = publishedModelEntry(window.location.search);
    if (!entry) return;
    setActiveModel(entry.modelId);
    useUiStore.getState().setWorkspace('scene');
    useUiStore.getState().setAppMode(entry.view);
  }, [setActiveModel]);

  useEffect(() => {
    if (!models || models.length === 0) return;
    if (!appliedUrlModelRef.current) {
      const qs = new URLSearchParams(window.location.search);
      const raw = (qs.get('model') || qs.get('m') || '').trim();
      if (raw) {
        const target = models.find(
          (m) =>
            m.id === raw ||
            (m.name ?? '').toLowerCase() === raw.toLowerCase(),
        );
        // `models` is only the FIRST PAGE of the catalog — a model id beyond
        // page 1 won't be found here, but the bundle query loads any model by
        // id, so trust the raw id as a fallback (only name-matching needs the
        // list).  Without this, ?model=<id> for any non-page-1 model silently
        // fell back to models[0].
        const resolvedId = target?.id ?? raw;
        if (activeModelId !== resolvedId) {
          setActiveModel(resolvedId);
          appliedUrlModelRef.current = true;
          return;
        }
      }
      appliedUrlModelRef.current = true;
    }
    if (!activeModelId) {
      const startupModel = firstUsableStartupModel(models);
      if (startupModel?.id) setActiveModel(startupModel.id);
    }
  }, [models, activeModelId, setActiveModel]);

  // Defensive reset: stale persist could keep destructive view flags after store rename;
  // on mount, make sure no destructive transforms leak in.
  useEffect(() => {
    const s = useViewerStore.getState();
    if (s.explode !== 0) s.setExplode(0);
    if (s.sectionAxis !== null) s.setSection(null);
    // hdri-фон больше не гасим принудительно: hdri процедурный (безопасен),
    // премиум-дефолт стора = сцена в небе (30.09), а гашение перетирало
    // ручной выбор юзера на каждой перезагрузке (наследие RGBE-эпохи, июнь).
    if (s.exposure > 1.05) s.setExposure(1);
  }, []);

  /* Глобальные клавиши вьюпорта живут отдельным хуком: клавиатура —
     самостоятельный орган управления, а не кусок корневого компонента, и её
     поведение обязано проверяться нажатием, а не чтением исходника. */
  useGlobalViewerKeys();

  return (
    <WorkbenchRuntimeProvider onStartTour={() => setShowOnboarding((prev) => !prev)}>
      <Suspense fallback={<div className="h-screen w-screen bg-bg" />}><LuminousShell /></Suspense>
      {/* Onboarding is intentionally manual-only (no auto-open). */}
      <Suspense fallback={null}>
        <CommandPalette />
        <ShortcutsScreen />
        <PanelDetailSheet />
        <BulkPanelDialog />
        <GlobalModalHost />
        {showOnboarding && <OnboardingTour onClose={() => setShowOnboarding(false)} />}
      </Suspense>
    </WorkbenchRuntimeProvider>
  );
}
